/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { type QueryFunctionContext, useQuery, type QueryClient } from '@tanstack/react-query';
import { type ContentStackBlogPostSummary } from '@/features/contentstack/interface';
import { getAxiosForClient } from '@/lib/axios';
import { getContentStackRelatedBlogContent } from '@/features/contentstack/lib/blog/getContentStackRelatedBlogContent.server'; // eslint-disable-line no-restricted-imports
import { type NextApiRequest } from 'next';
import { type PageContextType } from '@/next-types';

type RelatedBlogsParams = {
  primaryCategory: string;
  currentBlogUrl: string;
  postType: string;
};

export const getRelatedBlogsQueryKey = (params: RelatedBlogsParams) =>
  ['cms', 'related-blogs', params] as const;

export const fetchRelatedBlogs = async ({
  queryKey,
}: QueryFunctionContext<ReturnType<typeof getRelatedBlogsQueryKey>>) => {
  const [, , { primaryCategory, currentBlogUrl, postType }] = queryKey;

  const response = await getAxiosForClient().get<ContentStackBlogPostSummary[]>(
    '/api/contentstack/related-blogs',
    {
      params: { primaryCategory, currentBlogUrl, postType },
    },
  );

  return response.data || [];
};

export const useContentStackRelatedBlogsData = (options: RelatedBlogsParams) => {
  return useQuery({
    queryKey: getRelatedBlogsQueryKey(options),
    queryFn: fetchRelatedBlogs,
    enabled: Boolean(options.primaryCategory && options.currentBlogUrl && options.postType),
  });
};

export const prefetchContentStackRelatedBlogsData = async ({
  queryClient,
  params,
  req,
}: {
  queryClient: QueryClient;
  params: RelatedBlogsParams;
  req: NextApiRequest | PageContextType['req'];
}) => {
  if (!params.primaryCategory) {
    return;
  }

  return queryClient.prefetchQuery({
    queryKey: getRelatedBlogsQueryKey(params),
    queryFn: (context) => getContentStackRelatedBlogContent(req, context.queryKey[2]),
  });
};
